"use client";
import { useEffect, useRef } from "react";

const STEPS = [
  {
    n: 1,
    title: "Bulk screening",
    body: (
      <>
        Upload <strong>10,000 rows</strong> and watch them resolve live, with resumable export. Pipe the result straight into your KYC pipeline or download the audit-ready CSV.
      </>
    ),
    pill: "10K rows · CSV · resumable",
  },
  {
    n: 2,
    title: "Continuous watchlist",
    body: (
      <>
        Daily re-screen across your active book. Alerts fire on <strong>new match</strong>, <strong>removed match</strong>, or a score change above 0.10. Never silently.
      </>
    ),
    pill: "Daily refresh · 3 alert types",
  },
  {
    n: 3,
    title: "Search history",
    body: (
      <>
        Every screening replayable for <strong>90 days</strong>. Reopen the exact match card you cleared months ago, with one click. Exactly as the regulator will.
      </>
    ),
    pill: "90-day audit replay",
  },
  {
    n: 4,
    title: "REST API",
    body: (
      <>
        OpenAPI 3 spec, per-key tokens, rate limits, per-key audit logs. Wire screenings into your existing <strong>KYC stack</strong> in an afternoon.
      </>
    ),
    pill: "OpenAPI 3 · per-key tokens",
  },
];

const TITLE_WORDS = ["Made", "for", "the", "way", "compliance", "desks", "actually"];

export default function WorkflowSection() {
  const sectionRef = useRef(null);
  const headRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const head = headRef.current;
    if (!section) return;
    const dots = section.querySelectorAll(".workflow-dot");
    const N = 4;
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let cleanups = [];

    if (!reduce) {
      let ticking = false;
      const update = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const r = section.getBoundingClientRect();
          const vh = window.innerHeight;
          const runway = section.offsetHeight - vh;
          let p = runway > 0 ? -r.top / runway : 0;
          p = Math.max(0, Math.min(1, p));
          section.style.setProperty("--workflow-progress", p.toFixed(4));
          const activeIdx = Math.min(N - 1, Math.floor(p * N));
          dots.forEach((d, i) => d.classList.toggle("active", i === activeIdx));
          ticking = false;
        });
      };
      window.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update, { passive: true });
      update();
      cleanups.push(() => {
        window.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
      });
    }

    if (head && !reduce && "IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              head.classList.remove("in");
              // force reflow
              void head.offsetWidth;
              head.classList.add("in");
            } else {
              head.classList.remove("in");
            }
          });
        },
        { threshold: 0.2 }
      );
      io.observe(head);
      cleanups.push(() => io.disconnect());
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <section ref={sectionRef} className="workflow" id="workflow">
      <div className="workflow-pin">
        <div className="container-c">
          <div ref={headRef} className="section-head workflow-head">
            <span className="workflow-eyebrow">How teams use it</span>
            <h2 className="section-title workflow-title">
              {TITLE_WORDS.map((w, i) => (
                <span key={i} className="word">{w}</span>
              ))}
              <span className="word"><em>work.</em></span>
            </h2>
          </div>
          <div className="workflow-stage">
            {STEPS.map((s) => (
              <article
                key={s.n}
                className="workflow-item"
                style={{ "--i": s.n - 1 }}
              >
                <div className="workflow-num">{s.n}</div>
                <div className="workflow-body">
                  <h3 className="workflow-comp">{s.title}</h3>
                  <p className="workflow-text">{s.body}</p>
                  <span className="workflow-pill">{s.pill}</span>
                </div>
              </article>
            ))}

            <div className="workflow-dots" aria-hidden="true">
              {STEPS.map((s) => (
                <span key={s.n} className="workflow-dot" style={{ "--i": s.n - 1 }}></span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
